<template>
  <v-app data-prerender-ready="mayon-app">

    <v-app-bar app color="white" light elevate-on-scroll height="76" class="app-navbar">
      <router-link class="brand-button" :to="{ name: 'home', hash: '#hero' }" aria-label="Home" @click.native="setActive({ route: 'home', hash: 'hero' })">
        <v-img
          alt="Mayon Clinical Laboratory & Medical Services"
          class="brand-button__logo"
          contain
          :src="require('@/assets/Navbar/256.png')"
        />
        <span class="brand-button__text">
          <strong>Mayon</strong>
          <small>Clinical Laboratory & Medical Services</small>
        </span>
      </router-link>

      <v-spacer />

      <div class="desktop-nav">
        <router-link
          v-for="item in navigation"
          :key="item.label"
          class="nav-link"
          :class="{ 'is-active': isActiveNav(item) }"
          :to="linkTo(item)"
          @click.native="setActive(item)"
        >
          <span>{{ item.label }}</span>
        </router-link>
        <v-btn
          color="#2563eb"
          dark
          depressed
          rounded
          class="ml-2 nav-cta mayon-btn mayon-btn--primary"
          :to="'/branches'"
          @click.native="setActive({ path: '/branches' })"
        >
          Contact Branch
        </v-btn>      
      </div>

      <v-menu offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn class="mobile-menu-button" icon color="#111A2D" v-bind="attrs" v-on="on" aria-label="Open menu">
            <i class="pi pi-bars menu-icon" aria-hidden="true"></i>
          </v-btn>
        </template>
        <v-list class="mobile-menu-list">
          <v-list-item
            v-for="item in navigation"
            :key="item.label"
            :to="linkTo(item)"
            @click.native="setActive(item)"
          >
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </v-list-item>
          <v-list-item :href="portalLink.url" target="_blank" rel="noopener noreferrer">
            <v-list-item-title>{{ portalLink.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-main>
      <router-view />
    </v-main>

    <div class="floating-actions" aria-label="Quick contact actions">
      <v-menu offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn class="floating-actions__button floating-actions__button--viber" icon v-bind="attrs" v-on="on" aria-label="Open Viber options">
            <img src="@/assets/Navbar/viber.png" alt="Viber" />
          </v-btn>
        </template>
        <v-list class="bubble-menu">
          <v-list-item
            v-for="item in contactChannels.viber"
            :key="item.label"
            :href="item.href"
            target="_blank"
            rel="noopener noreferrer"
          >
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-menu offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn class="floating-actions__button floating-actions__button--whatsapp" icon v-bind="attrs" v-on="on" aria-label="Open WhatsApp options">
            <img src="@/assets/Navbar/whatsapp.png" alt="WhatsApp" />
          </v-btn>
        </template>
        <v-list class="bubble-menu">
          <v-list-item
            v-for="item in contactChannels.whatsapp"
            :key="item.label"
            :href="item.href"
            target="_blank"
            rel="noopener noreferrer"
          >
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <Footer />
  </v-app>
</template>

<script>
import Footer from './views/Footer.vue'
import { contactChannels, navigation, portalLink } from '@/data/mayonSite'

export default {
  name: 'App',
  components: { Footer },
  data() {
    return {
      activeNavKey: 'home:hero',
      contactChannels,
      navigation,
      portalLink
    }
  },
  watch: {
    $route() {
      this.setActiveFromRoute()
    }
  },
  mounted() {
    this.setActiveFromRoute()
  },
  methods: {
    navKey(item) {
      return item.path || `${item.route}:${item.hash || ''}`
    },
    isActiveNav(item) {
      return this.activeNavKey === this.navKey(item)
    },
    setActiveFromRoute() {
      if (this.$route.path.startsWith('/branches')) {
        this.activeNavKey = '/branches'
        return
      }

      if (this.$route.name === 'about') {
        this.activeNavKey = 'about:about'
        return
      }

      if (this.$route.name === 'home') {
        this.activeNavKey = `home:${this.$route.hash ? this.$route.hash.slice(1) : 'hero'}`
        return
      }

      this.activeNavKey = ''
    },
    linkTo(item) {
      if (item.path) return item.path
      return { name: item.route, hash: item.route === 'about' ? undefined : (item.hash ? `#${item.hash}` : undefined) }
    },
    setActive(item) {
      this.activeNavKey = this.navKey(item)
    }
  }
}
</script>

<style lang="scss">
:root {
  --mayon-navy: #111a2d;
  --mayon-blue: #2563eb;
  --mayon-sky: #4fc3f7;
  --mayon-teal: #14b8a6;
  --mayon-bg: #f8fafc;
  --mayon-surface: #ffffff;
  --mayon-text: #101828;
  --mayon-muted: #667085;
  --mayon-border: #e5e7eb;
}

html {
  scroll-behavior: smooth;
}

body {
  background: var(--mayon-bg);
  color: var(--mayon-text);
}

.v-application {
  font-family: Roboto, Helvetica, sans-serif !important;
  color: var(--mayon-text) !important;
}

.mayon-btn.v-btn {
  border-radius: 999px !important;
  box-shadow: none !important;
  font-size: 0.78rem !important;
  font-weight: 900 !important;
  letter-spacing: 0.08em !important;
  min-height: 44px !important;
  padding: 0 1.25rem !important;
  text-transform: uppercase !important;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, background 0.18s ease !important;
}

.mayon-btn.v-btn:hover,
.mayon-btn.v-btn:focus {
  transform: translateY(-1px);
}

.mayon-btn--primary.v-btn {
  background: #14b8a6 !important;
  border: 1px solid rgba(37, 99, 235, 0.2) !important;
  color: #ffffff !important;
}

.mayon-btn--primary.v-btn:hover,
.mayon-btn--primary.v-btn:focus {
  box-shadow: 0 16px 34px rgba(37, 99, 235, 0.25) !important;
}

.mayon-btn--secondary.v-btn {
  background: rgba(255, 255, 255, 0.92) !important;
  border: 1px solid #14b8a6 !important;
  color: #14b8a6 !important;
}

.mayon-btn--secondary.v-btn:hover,
.mayon-btn--secondary.v-btn:focus {
  background: #ffffff !important;
  box-shadow: 0 14px 30px rgba(17, 26, 45, 0.1) !important;
}

.mayon-btn--ghost.v-btn {
  background: transparent !important;
  border: 1px solid transparent !important;
  color: var(--mayon-blue) !important;
  padding-left: 0.75rem !important;
  padding-right: 0.75rem !important;
}

.mayon-btn--light.v-btn {
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.82) !important;
  color: #ffffff !important;
}

.mayon-btn--block.v-btn {
  width: 100%;
}


.top-utility-bar {
  background: var(--mayon-navy);
  color: #ffffff;
  font-size: 0.86rem;
  padding: 0.45rem 1rem;
  position: relative;
  z-index: 8;

  &__inner {
    align-items: center;
    display: flex;
    gap: 1rem;
    justify-content: center;
    margin: 0 auto;
    max-width: 1180px;
  }

  &__item,
  &__portal {
    align-items: center;
    color: #ffffff;
    display: inline-flex;
    gap: 0.35rem;
    text-decoration: none;
    white-space: nowrap;
  }

  &__portal {
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 999px;
    font-weight: 700;
    margin-left: auto;
    padding: 0.2rem 0.75rem;
  }
}

.app-navbar {
  border-bottom: 1px solid var(--mayon-border) !important;
  box-shadow: 0 12px 34px rgba(17, 26, 45, 0.05) !important;
}

.brand-button {
  align-items: center;
  background: transparent;
  border: 0;
  color: var(--mayon-navy);
  cursor: pointer;
  display: inline-flex;
  gap: 0.8rem;
  padding: 0;
  text-align: left;
  text-decoration: none !important;

  &__logo {
    height: 48px;
    width: 48px;
  }

  &__text {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
    

    strong {
      font-size: 1.1rem;
      letter-spacing: -0.02em;
    }

    small {
      color: var(--mayon-muted);
      font-size: 0.76rem;
      font-weight: 700;
      text-transform: uppercase;
    }
  }
  
}

.desktop-nav {
  align-items: center;
  display: flex;
  gap: 0.35rem;
}

.nav-link {
  background: transparent;
  border: 0;
  color: var(--mayon-navy);
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  padding: 0.7rem 0.7rem;
  position: relative;
  transition: color 0.2s ease, transform 0.2s ease;
  text-decoration: none !important;


  span {
    position: relative;
    z-index: 1;
  }

  &::after {
    background: linear-gradient(90deg, var(--mayon-blue), var(--mayon-teal));
    border-radius: 999px;
    bottom: 0.38rem;
    content: "";
    height: 3px;
    left: 0.7rem;
    opacity: 0;
    position: absolute;
    right: 0.7rem;
    transform: scaleX(0);
    transform-origin: center;
    transition: transform 0.24s ease, opacity 0.24s ease;
  }

  &:hover,
  &.is-active {
    color: var(--mayon-blue);
    transform: translateY(-1px);
  }

  &:hover::after,
  &.is-active::after {
    opacity: 1;
    transform: scaleX(1);
  }
}

.nav-cta {
  font-size: 0.82rem !important;
  font-weight: 900 !important;
  letter-spacing: 0.02em !important;
}

.mobile-menu-button {
  display: none !important;

  .menu-icon {
    font-size: 1.6rem;
    line-height: 1;
  }
}

.mobile-menu-list .v-list-item__title {
  font-weight: 700;
}

.floating-actions {
  bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  position: fixed;
  right: 1.5rem;
  z-index: 100;

  &__button {
    background: #ffffff !important;
    box-shadow: 0 14px 32px rgba(17, 26, 45, 0.2) !important;
    height: 56px !important;
    width: 56px !important;

    img {
      height: 62px;
      object-fit: contain;
      width: 62px;
    }
  }
}

.bubble-menu {
  border-radius: 16px !important;
  min-width: 240px;
}

@media (max-width: 1023px) {
  .top-utility-bar {
    display: none;
  }

  .desktop-nav {
    display: none;
  }

  .mobile-menu-button {
    display: inline-flex !important;
  }

  .brand-button__text small {
    display: none;
  }

  .floating-actions {
    bottom: 1rem;
    right: 1rem;
  }
  
}
</style>
